import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CHAIN_IDS, SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { fetchJsonOrThrow } from '@/utils/fetch';
import { getPortfolioRefreshNonce } from '@/utils/portfolioRefresh';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';
import type { TokenBalance } from '@/utils/zerion/types';

interface WalletPositionsResponse {
  balances: Record<number, Array<TokenBalance>>;
}

interface UseWalletPositionsParams {
  evmAddress?: string;
  solanaAddress?: string;
  enabled?: boolean;
}

const fetchPositions = (url: string) => fetchJsonOrThrow<WalletPositionsResponse>(url);

function buildUrl({
  evmAddress,
  solanaAddress,
  refreshNonce,
}: {
  evmAddress?: string;
  solanaAddress?: string;
  refreshNonce: string;
}): string | null {
  if (!evmAddress && !solanaAddress) return null;

  // EVM-only: skip Solana chain. Solana-only: just Solana. Both: every supported chain.
  let chains: number[];
  if (evmAddress && solanaAddress) {
    chains = [...SUPPORTED_CHAIN_IDS];
  } else if (evmAddress) {
    chains = SUPPORTED_CHAIN_IDS.filter((id) => id !== CHAIN_IDS.SOLANA);
  } else {
    chains = [CHAIN_IDS.SOLANA];
  }

  const params = new URLSearchParams();
  if (evmAddress) params.set('evmAddress', evmAddress);
  if (solanaAddress) params.set('solanaAddress', solanaAddress);
  params.set('chains', chains.join(','));
  if (refreshNonce) params.set('_refresh', refreshNonce);
  return `${routes.api.WALLET_POSITIONS}?${params.toString()}`;
}

export const walletPositionsQueryKey = (params: { evmAddress?: string; solanaAddress?: string }) =>
  ['wallet-positions', params] as const;

export function useWalletPositions({
  evmAddress,
  solanaAddress,
  enabled = true,
}: UseWalletPositionsParams) {
  const params = { evmAddress, solanaAddress };

  const { data, ...rest } = useQuery<WalletPositionsResponse, Error>({
    queryKey: walletPositionsQueryKey(params),
    queryFn: () =>
      fetchPositions(buildUrl({ ...params, refreshNonce: getPortfolioRefreshNonce() })!),
    enabled: enabled && buildUrl({ ...params, refreshNonce: '' }) !== null,
    staleTime: 60_000,
    retry: retryNetworkOrServerError,
    // `refetchIntervalInBackground` defaults to `false`,
    // so RQ skips ticks while the tab is hidden.
    refetchInterval: 60_000,
    // Override the global `refetchOnWindowFocus: false` so positions
    // refresh when the user returns to the tab; `staleTime` above
    // gates rapid re-focuses.
    refetchOnWindowFocus: true,
  });

  const { allTokens, totalValueUsd } = useMemo(() => {
    const tokens: TokenBalance[] = [];
    if (data?.balances) {
      for (const [, list] of Object.entries(data.balances)) {
        tokens.push(...list);
      }
    }

    // Consumers rely on value-desc order (small-balance split, PnL batching).
    tokens.sort((a, b) => (b.balanceUsd ?? 0) - (a.balanceUsd ?? 0));

    const total = tokens.reduce((sum, t) => sum + (t.balanceUsd ?? 0), 0);

    return { allTokens: tokens, totalValueUsd: total };
  }, [data]);

  return {
    allTokens,
    totalValueUsd,
    ...rest,
  };
}
